/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  Component,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {InputDisplayModeEnum} from '../../shared/input/input-display-mode.enum';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {PartNumberChangesDetailSolutionState} from './part-number-changes-detail-solution.state';
import {PartNumberChangeSolutionMaterialDetailModel} from '../models/part-number-change-solution-material-detail.model';
import { v4 as uuid } from 'uuid';
import {
    Subject,
    Subscription
} from 'rxjs';
import {PartNumberChangesSolutionConfigurationModel} from '../services/part-number-changes-detail-configuration/models/pnc-trial/part-number-changes-solution-configuration.model';
import {PartNumberChangesDetailTrialDisplayModeEnum} from '../services/part-number-changes-detail-configuration/models/pnc-trial/part-number-changes-detail-trial-display-mode.enum';
import {WarningsEnum} from '../../shared/warnings/warnings.enum';
import {RouteSearchCriteriaModel} from '../../api/ms-partnumberchanges/contracts/route-search-criteria.model';
import {FeatureToggleService} from '../../shared/guards/feature-toggle/feature-toggle.service';
import {
    AbstractControl,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {InputMessagesService} from '../../shared/input/service/input-messages.service';
import {InputTextComponent} from '../../shared/input/input-text/input-text.component';
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {
    PartNumberChangesChangePlantPopupComponent
} from "./part-number-changes-change-plant-popup/part-number-changes-change-plant-popup.component";
import {SupplyingPackingPlantBaseModel} from "../models/supplying-packing-plant/supplying-packing-plant-base.model";
import {PrettyPrintReferencePipe} from "../../shared/pipes/pretty-print-reference/pretty-print-reference.pipe";

@Component({
    selector: 'app-part-number-changes-detail-solution',
    templateUrl: './part-number-changes-detail-solution.component.html',
    styleUrls: [
        '../styles/pnc-base.component.css',
        './part-number-changes-detail-solution.component.css',
        '../styles/pnc-base-solution.component.css'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartNumberChangesDetailSolutionComponent
        extends StatefulComponent<PartNumberChangesDetailSolutionState>
        implements OnInit {
    @Input() supplyingPackingPlantCode: string;
    @Input() solutionSupplyingPackingPlant: SupplyingPackingPlantBaseModel;
    @Input() alternativeSupplyingPackingPlants: SupplyingPackingPlantBaseModel[];
    @Input() isPncTypePartnerOrPlanner: boolean;
    @Input() solutionTrialNumber: number;
    @Input() showSolutionSpecialist: boolean;
    @Input() updateRouteSearchCriteriaChange: Subject<RouteSearchCriteriaModel> =
            new Subject<RouteSearchCriteriaModel>();
    @Input() triggerFormReset: Subject<void> = new Subject<void>();
    @Input() configuration: PartNumberChangesSolutionConfigurationModel;
    @Output() solution: Subject<PartNumberChangesDetailSolutionState> =
            new Subject<PartNumberChangesDetailSolutionState>();
    public supplyingPackingPlantCodeChange: Subject<string>;
    private subscriptions = new Subscription();
    validators: { [key: string]: ValidatorFn[] } = {
        iLevel: [Validators.maxLength(14), Validators.pattern(/^[A-Za-z][0-9]{2}[A-Za-z]-([0-9]{2}-){2}[0-9]{3}$/)]
    };
    public iLevelMessages = new Map<WarningsEnum, string[]>();
    @ViewChild('iLevelInput') iLevelInput: InputTextComponent;

    public prettySolutionSupplyingPackingPlant: string;

    openPopup() {
        const modalRef = this.modalService.open(PartNumberChangesChangePlantPopupComponent);
        modalRef.componentInstance.initialData = this.configuration;
        modalRef.result.then(
            (data) => {
                this.prettySolutionSupplyingPackingPlant = this.prettyPrintReferencePipe.transform(data);
                this.storeState({
                    solutionSupplyingPackingPlant: data
                });
            }
        )
    }

    get canAddAdditionalParts(): boolean {
        return this.configuration?.displayConfiguration.partNumberChangeConfig?.maxNumberOfAdditionalMaterials
                > (this.state?.additionalMaterials ?? []).length && !this.showInViewMode && !this.disableComponent;
    }

    get hasDisplayConfiguration(): boolean {
        return this.configuration != null;
    }

    get globalStyleClass(): string {
        let styleClass = 'pnc-solution';

        if (this.configuration?.displayConfiguration?.displayMode != null) {
            styleClass += ` ${this.configuration?.displayConfiguration?.displayMode}`;
        }

        if (this.state.isRequiresFlashingToggleActive || this.showInViewSolutionMode || this.showRequiresFlashingToggleInViewMode) {
            styleClass += ` requires-flashing`;
        }

        if (this.showSpecialistSolution) {
            styleClass += ` requires-flashing-solution`;
        }

        return styleClass;
    }

    get getCurrentPNCCount(): string {
        let count;
        if (this.solutionTrialNumber > 0) {
            count = `Trial ${this.solutionTrialNumber}`;
        } else {
            count = 'Trial 1';
        }
        return !this.showSpecialistSolution ? count : count + ' (Accepted)';
    }

    get showInViewMode(): boolean {
        return this.configuration?.displayConfiguration.displayMode === PartNumberChangesDetailTrialDisplayModeEnum.DETAIL;
    }

    get invertColors(): boolean {
        return this.showInViewMode && !this.showSpecialistSolution;
    }

    get showInViewSolutionMode(): boolean {
        return this.configuration?.displayConfiguration?.displayMode === PartNumberChangesDetailTrialDisplayModeEnum.DETAIL_SOLUTION;
    }

    get showSpecialistSolution(): boolean {
        return this.configuration?.isSolutionEE;
    }

    get checkboxTextStyle(): string {
        return this.showSpecialistSolution ? 'pnc-solution-checkbox-text-ee-solution' : 'pnc-solution-checkbox-text';
    }

    get showAdditionalFields(): boolean {
        return this.showInViewMode && !this.showSpecialistSolution;
    }

    get showFlashingFlag(): boolean {
        return this.showViewSolutionEEParts && !this.showSpecialistSolution;
    }

    get showRequiresFlashingToggleInViewMode(): boolean {
        return this.showInViewSolutionMode || this.configuration?.requiresFlashing;
    }

    get showViewSolutionEEParts(): boolean {
        return this.showInViewMode && this.configuration?.requiresFlashing;
    }

    get showEESpecialistValidation(): boolean {
        return this.featureToggleService.isEnabled(['feature.toggle.ee-specialist'])
                && (!this.showInViewMode || this.showInViewSolutionMode) || this.showSpecialistSolution;
    }

    get additionalPartNumbers(): PartNumberChangeSolutionMaterialDetailModel[] {
        let partNumbers = this.state?.additionalMaterials;

        if (partNumbers == null || this.showInViewMode || this.showInViewSolutionMode) {
            partNumbers = this.configuration?.data.additionalMaterials;
        }
        return partNumbers;
    }

    get tooltipMessage(): string {
        return `You can't add more than ` +
                this.configuration?.displayConfiguration.partNumberChangeConfig?.maxNumberOfAdditionalMaterials
                + ' additional part numbers.';
    }

    get disableComponent(): boolean {
        return this.state.supplyingPackingPlantCode == null;
    }

    get eeSolutionAdditionalInformation(): string {
        return this.showSpecialistSolution ? this.configuration?.data?.additionalInformation
                : this.configuration?.data?.eeAdditionalInformation;
    }

    get currentPackingPlant(): string {
        if (this.state.solutionSupplyingPackingPlant) {
            return this.state.solutionSupplyingPackingPlant.code;
        } else if (this.state.supplyingPackingPlantCode) {
            return this.state.supplyingPackingPlantCode;
        } else {
            return this.supplyingPackingPlantCode;
        }
    }

    constructor(
        private featureToggleService: FeatureToggleService,
        private inputMessagesService: InputMessagesService,
        private prettyPrintReferencePipe: PrettyPrintReferencePipe,
        private modalService: NgbModal) {
        super();
    }

    ngOnInit(): void {
        this.storeState({
            material: {isValid: false, id: undefined, material: undefined, quantity: undefined},
            supplyingPackingPlantCode: this.supplyingPackingPlantCode,
            alternativeSupplyingPackingPlants: this.alternativeSupplyingPackingPlants,
            requiresFlashing: this.configuration?.requiresFlashing,
            additionalMaterials: this.configuration?.data?.additionalMaterials ?? [],
            additionalInformation: this.configuration?.data?.additionalInformation ?? undefined,
            eeSolution: {additionalInformationSolution: null, iLevel: null, isValid: true}
        });

        if (this.updateRouteSearchCriteriaChange != null) {
            this.supplyingPackingPlantCodeChange = new Subject<string>();
            this.setRouteSearchCriteriaSubscription();
        }

        this.prettySolutionSupplyingPackingPlant = this.prettyPrintReferencePipe
            .transform(this.solutionSupplyingPackingPlant
                ? this.solutionSupplyingPackingPlant
                : this.alternativeSupplyingPackingPlants
                    .find(x => x.code == this.supplyingPackingPlantCode));
    }

    toggleRequiresFlashing(requiresFlashing: Event): void {
        const requiresFlashingValue = (<HTMLInputElement>requiresFlashing.target).checked;
        this.storeState({isRequiresFlashingToggleActive: requiresFlashingValue});
    }

    trackByItem = (index: number, item: PartNumberChangeSolutionMaterialDetailModel): string => item.id;

    /**
     * Sets the new problem description value
     */
    setAdditionalInformation(additionalInformation: string): void {
        this.storeState({additionalInformation: additionalInformation});
    }

    storeState(state?: Partial<PartNumberChangesDetailSolutionState>): void {
        super.storeState(state);

        super.storeState({
            isValid: this.state.material?.isValid && (this.state.additionalMaterials?.every((m) => m.isValid) ?? true)
        });

        this.solution.next(this.state);
    }

    addPartNumber(): void {
        this.storeState({
            additionalMaterials: [
                ...this.state.additionalMaterials ?? [], {
                    material: null,
                    quantity: null,
                    isValid: false,
                    id: uuid()
                }
            ]
        });
    }

    removePartNumber(event: PartNumberChangeSolutionMaterialDetailModel): void {
        this.storeState({
            additionalMaterials: this.state.additionalMaterials?.filter((value) => value.id !== event.id)
        });
    }

    newPartChange(event: PartNumberChangeSolutionMaterialDetailModel): void {
        this.storeState({
            material: event
        });
    }

    additionalPartChange(event: PartNumberChangeSolutionMaterialDetailModel): void {
        this.storeState({
            additionalMaterials: this.state.additionalMaterials?.map((value) => {
                if (value.id === event.id) {
                    return event;
                } else {
                    return value;
                }
            })
        });
    }

    initialValuePipe(displayMode: InputDisplayModeEnum, additionalInformation: string): string {
        let pipeValue = additionalInformation;
        if (displayMode === InputDisplayModeEnum.VIEW && (additionalInformation == null || additionalInformation === '')) {
            pipeValue = '-';
        }
        return pipeValue;
    }

    warningLevel(required: boolean): WarningsEnum {
        return required ? WarningsEnum.WARNING_PNC_SEND : null;
    }

    setAdditionalInformationSolution(additionalInformationSolution: string): void {
        this.storeState({
            eeSolution: {
                ...this.state.eeSolution,
                additionalInformationSolution: additionalInformationSolution === '' ? null : additionalInformationSolution
            }
        });
    }

    setILevel(iLevel: string): void {
        this.storeState({
            eeSolution: {
                ...this.state.eeSolution,
                iLevel: iLevel ? iLevel.toUpperCase() : null
            }
        });
    }

    iLevelStateChange = (control: AbstractControl): void => {
        this.inputMessagesService.invalidInputValueError(control,
                this.iLevelMessages,
                this.iLevelInput?.label);
        this.iLevelMessages = new Map(this.iLevelMessages);
        this.storeState({
            eeSolution: {
                ...this.state.eeSolution,
                isValid: this.iLevelMessages.get(WarningsEnum.ERROR).length === 0
            }
        });
    };

    private setRouteSearchCriteriaSubscription(): void {
        this.subscriptions.add(
                this.updateRouteSearchCriteriaChange
                        .subscribe((routeCriteria: RouteSearchCriteriaModel) => {
                            this.storeState({supplyingPackingPlantCode: routeCriteria?.supplyingPackingPlantCode});
                            this.supplyingPackingPlantCodeChange.next(this.state.supplyingPackingPlantCode);
                        })
        );
    }
}
